// Detects known active ingredients in free-form text and resolves the best
// matching product in our DB via a lightweight ilike-based lookup.
//
// Used by Glow Report insights, AdaptiveRoutineBanner, and Routines step cards
// to surface tappable "Find dupe" product chips.

import { supabase } from "./supabase";

// Canonical list of ingredients we recognise. Add more freely.
export const KNOWN_ACTIVES = [
  "Vitamin C",
  "Niacinamide",
  "Retinol",
  "Retinoid",
  "Bakuchiol",
  "Hyaluronic Acid",
  "Salicylic Acid",
  "Glycolic Acid",
  "Lactic Acid",
  "Mandelic Acid",
  "Azelaic Acid",
  "Tranexamic Acid",
  "Ceramide",
  "Peptide",
  "Squalane",
  "Snail Mucin",
  "Centella Asiatica",
  "Alpha Arbutin",
  "Kojic Acid",
  "Glutathione",
  "Rosehip Oil",
  "Argan Oil",
  "Jamaican Black Castor Oil",
  "Castor Oil",
  "Jojoba Oil",
  "Shea Butter",
  "Aloe Vera",
  "Tea Tree",
  "Green Tea",
  "Rosemary Oil",
];

// Case-insensitive scan — returns canonical strings, no duplicates, in
// order of first appearance.
export function detectActives(text = "") {
  if (!text) return [];
  const seen = new Set();
  const out = [];
  for (const k of KNOWN_ACTIVES) {
    const re = new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    if (re.test(text) && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

// Module-level cache keyed by `${ingredient}::${skinType}` so recommendations
// vary correctly per user profile.
const cache = new Map();

// STRICT match: only returns a product whose `skin_types[]` array contains the
// user's exact skin_type (lowercased). If none exists we return null — we do
// NOT fall back to any generic product, so a "Dry"-skinned user will never
// see a product tagged only for "Oily" skin.
//
// `skinType` should be the raw user profile value like "Dry", "Oily",
// "Combination", "Sensitive", "Normal", "Acne-prone". Case-insensitive.
export async function findProductForIngredient(ingredient, skinType = null) {
  const key = `${ingredient.toLowerCase()}::${(skinType || "any").toLowerCase()}`;
  if (cache.has(key)) return cache.get(key);

  const ingLower = ingredient.toLowerCase();
  const skinLower = (skinType || "").toLowerCase().trim();
  // Allow universally-safe products ("all") while still blocking products
  // tagged only for other skin types.
  const skinTokens = skinLower ? [skinLower, "all"] : null;

  // Strategy 1 — hero_ingredients array contains the ingredient AND (if a
  // skin_type is provided) skin_types[] contains the user's skin type or "all".
  let q1 = supabase
    .from("products")
    .select("name, brand, price_tier, price_usd, category, skin_types")
    .contains("hero_ingredients", [ingLower]);
  if (skinTokens) q1 = q1.overlaps("skin_types", skinTokens);
  const { data: hero } = await q1.limit(1).maybeSingle();
  if (hero) {
    cache.set(key, hero);
    return hero;
  }

  // Strategy 2 — name ilike ingredient AND skin filter (if provided).
  let q2 = supabase
    .from("products")
    .select("name, brand, price_tier, price_usd, category, skin_types")
    .ilike("name", `%${ingredient}%`);
  if (skinTokens) q2 = q2.overlaps("skin_types", skinTokens);
  const { data: byName } = await q2.limit(1).maybeSingle();
  cache.set(key, byName || null);
  return byName || null;
}
